import { withAuthHeaders } from './auth';
import { reportInvalidAuthFromError, reportInvalidAuthIfNeeded } from './authSession';
import { CapacitorHttp } from '@capacitor/core';
import { apiUrl, getApiBaseUrl, isNativeCapacitorRuntime } from '../config/appConfig';

/**
 * DATABASE API MODULE - Backend Communication Layer for Medical Monitoring System
 * 
 * This module provides comprehensive API functions for communicating with the
 * PostgreSQL/TimescaleDB backend server. It handles all database operations
 * including vital signs data retrieval, patient management, and medical monitoring.
 * 
 * Key Features:
 * - Retry logic for robust API communication with exponential backoff
 * - Health monitoring and connection status tracking
 * - Comprehensive error handling and user feedback
 * - Support for both real medical data and demo data generation
 * - Patient-specific data filtering and management
 * - Real-time vital signs monitoring and critical event alerting
 * - Backend health monitoring with automatic reconnection
 * 
 * API Endpoints Covered:
 * - Database connection and schema information
 * - Vital signs data retrieval (latest and historical)
 * - Patient and doctor management
 * - Medical thresholds and medication management
 * - Critical events and alerting system
 * - Demo data generation for testing and development
 * - Raw sensor data (ECG, PPG, temperature) processing
 * 
 * Architecture:
 * - Uses fetch API with retry logic for network resilience
 * - Implements health monitoring with listener pattern
 * - Supports both real-time and historical data queries
 * - Handles patient-specific data filtering
 * - Provides demo data for testing and development
 */

// Resolve at call time so native detection is correct after Capacitor bootstraps.
const resolveApiBaseUrl = () => getApiBaseUrl();

// Backend health monitoring system for connection status tracking
let backendHealthy = false;                    // Current backend health status
let healthIntervalHandle: number | null = null; // Interval timer for periodic health checks
const healthListeners: Array<() => void> = []; // Array of callback functions for health status changes

/**
 * UTILITY FUNCTION: Sleep/Delay for Retry Logic
 * 
 * Creates a promise that resolves after the specified number of milliseconds.
 * Used in retry logic to implement delays between retry attempts.
 * 
 * @param {number} ms - Number of milliseconds to wait
 * @returns {Promise<void>} - Promise that resolves after the delay
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const headersToRecord = (headers: HeadersInit | undefined): Record<string, string> => {
  if (!headers) return {};
  const normalized = new Headers(headers);
  const result: Record<string, string> = {};
  normalized.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

/**
 * UTILITY FUNCTION: Fetch with Retry Logic and Exponential Backoff
 * 
 * Implements robust API communication with automatic retry logic for failed requests.
 * Uses exponential backoff to handle transient network issues and server errors.
 * 
 * Retry Strategy:
 * - Retries on HTTP status codes 502, 503, 504 (server errors)
 * - Uses exponential backoff with maximum delay of 8 seconds
 * - Maximum of 5 retry attempts by default
 * - Handles network timeouts and connection issues
 * 
 * @param {RequestInfo} input - URL or Request object for the API call
 * @param {RequestInit} init - Optional fetch configuration (headers, method, etc.)
 * @param {number} maxRetries - Maximum number of retry attempts (default: 5)
 * @returns {Promise<Response>} - Fetch response object
 */
const fetchWithRetry = async (input: RequestInfo, init: RequestInit = {}, maxRetries: number = 5) => {
	let attempt = 0;                    // Current retry attempt counter
	let lastError: unknown = null;       // Last error encountered for debugging
	
	// Retry loop with exponential backoff
	while (attempt <= maxRetries) {
		try {
			const res = await fetch(input, init);
			if (!res.ok) {
				// Retry on transient server errors (502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout)
				if ([502, 503, 504].includes(res.status) && attempt < maxRetries) {
					attempt++;
					const delay = Math.min(1000 * 2 ** attempt, 8000);  // Exponential backoff with 8-second max delay
					await sleep(delay);
					continue;
				}
				return res; // let caller handle non-ok
			}
			return res;
		} catch (err) {
			lastError = err;
			if (attempt >= maxRetries) break;
			attempt++;
			const delay = Math.min(1000 * 2 ** attempt, 8000);
			await sleep(delay);
		}
	}
	throw lastError instanceof Error ? lastError : new Error('Network error');
};

const notifyHealthy = () => {
	for (const fn of healthListeners) {
		try { fn(); } catch {}
	}
};

export const onBackendHealthy = (callback: () => void) => {
	healthListeners.push(callback);
	// If already healthy, fire immediately
	if (backendHealthy) {
		try { callback(); } catch {}
	}
};

export const isBackendHealthy = () => backendHealthy;

export const startHealthMonitor = () => {
	if (healthIntervalHandle != null) return; // already started
	const check = async () => {
		try {
			const res = await fetchWithRetry(`${resolveApiBaseUrl()}/health`, { method: 'GET' }, 0);
			const ok = res.ok;
			if (ok && !backendHealthy) {
				backendHealthy = true;
				notifyHealthy();
			} else if (!ok && backendHealthy) {
				backendHealthy = false;
			}
		} catch {
			backendHealthy = false;
		}
	};
	// immediate check and then poll every 2s
	check();
	healthIntervalHandle = setInterval(check, 2000) as unknown as number;
	// also retry on window focus
	if (typeof window !== 'undefined') {
		window.addEventListener('focus', check);
	}
};

// Helper function for API calls (now with retry and health awareness)
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
	try {
		const headers = withAuthHeaders({
			'Content-Type': 'application/json',
			...(options.headers || {}),
		});
		const requestUrl = `${resolveApiBaseUrl()}${endpoint}`;

		const response = await fetchWithRetry(requestUrl, {
			...options,
			headers,
		});

		if (!response.ok) {
			const errorText = await response.text();
			let message = `HTTP error! status: ${response.status}`;
			try {
				const errorData = JSON.parse(errorText);
				message = errorData.error || errorData.message || message;
			} catch {
				if (errorText) message = errorText;
			}
			reportInvalidAuthIfNeeded(response.status, message);
			throw new Error(message);
		}

		return await response.json();
	} catch (error) {
		// Native WebView can fail cross-origin HTTP fetches; retry with CapacitorHttp.
		if (isNativeCapacitorRuntime) {
			try {
				const headers = withAuthHeaders({
					'Content-Type': 'application/json',
					...(options.headers || {}),
				});
				const requestUrl = `${resolveApiBaseUrl()}${endpoint}`;
				const nativeResponse = await CapacitorHttp.request({
					url: requestUrl,
					method: (options.method || 'GET').toUpperCase(),
					headers: headersToRecord(headers),
					data: options.body
						? (typeof options.body === 'string'
							? (() => { try { return JSON.parse(options.body); } catch { return options.body; } })()
							: options.body)
						: undefined
				});

				if (nativeResponse.status < 200 || nativeResponse.status >= 300) {
					let message = `HTTP error! status: ${nativeResponse.status}`;
					if (typeof nativeResponse.data === 'string') {
						try {
							const errorData = JSON.parse(nativeResponse.data);
							message = errorData.error || errorData.message || message;
						} catch {
							if (nativeResponse.data) message = nativeResponse.data;
						}
					} else if (nativeResponse.data && typeof nativeResponse.data === 'object') {
						const d = nativeResponse.data as Record<string, any>;
						message = d.error || d.message || message;
					}
					reportInvalidAuthIfNeeded(nativeResponse.status, message);
					throw new Error(message);
				}

				if (typeof nativeResponse.data === 'string') {
					return nativeResponse.data ? JSON.parse(nativeResponse.data) : {};
				}
				return nativeResponse.data ?? {};
			} catch (nativeError) {
				console.error(`API call to ${endpoint} failed:`, nativeError);
				throw nativeError;
			}
		}
		reportInvalidAuthFromError(error);
		console.error(`API call to ${endpoint} failed:`, error);
		throw error;
	}
};

export const getVitalSigns = async (limit: number = 10, patId?: string): Promise<any[]> => {
  try {
    console.log(`Fetching vital signs data1 (limit: ${limit})...`);
    const qs = new URLSearchParams({ limit: String(limit) });
    if (patId){
      qs.append('pat_id', patId);
    }else{
      return [];
    }
    const data = await apiCall(`/vital-signs?${qs.toString()}`);
    console.log('Vital signs data fetched1:', data);
    return data as any[];
  } catch (error) {
    console.error('Error fetching vital signs:', error);
    throw error;
  }
};

export const getVitalSignsRange = async (startIso: string, endIso: string, patId?: string): Promise<any[]> => {
  try {
    console.log('Fetching vital signs in range:', startIso, '→', endIso);
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/vital-signs/range?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching vital signs range:', error);
    throw error;
  }
};

export const getVitalSignsRangeHours = async (hours: number, patId?: string): Promise<any[]> => {
  try {
    console.log('Fetching vital signs for last hours:', hours);
    const qs = new URLSearchParams({ hours: String(hours) });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/vital-signs/range-hours?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching vital signs range-hours:', error);
    throw error;
  }
};

/** One row from vitals_stats_wide_vw: min/median/max (and invalid_pct) for HR, HRV, RR, BP, SpO2 */
export type VitalsStatsWideRow = {
  pat_id: string;
  min_heart_rate?: number | null;
  max_heart_rate?: number | null;
  median_heart_rate?: number | null;
  heart_rate_invalid_pct?: number | null;
  min_hrv?: number | null;
  max_hrv?: number | null;
  median_hrv?: number | null;
  hrv_invalid_pct?: number | null;
  min_respiratory_rate?: number | null;
  max_respiratory_rate?: number | null;
  median_respiratory_rate?: number | null;
  respiratory_rate_invalid_pct?: number | null;
  min_systolic_bp?: number | null;
  max_systolic_bp?: number | null;
  median_systolic_bp?: number | null;
  systolic_bp_invalid_pct?: number | null;
  min_diastolic_bp?: number | null;
  max_diastolic_bp?: number | null;
  median_diastolic_bp?: number | null;
  diastolic_bp_invalid_pct?: number | null;
  min_spo2?: number | null;
  max_spo2?: number | null;
  median_spo2?: number | null;
  spo2_invalid_pct?: number | null;
  /** Critical events (yellow) count for the week — bigint from view */
  yellow_critical?: number | string | null;
  /** Critical events (red) count for the week — bigint from view */
  red_critical?: number | string | null;
};

/** range: 7d = vitals_stats_wide_vw, 24h = vitals_stats_wide_last24h_vw */
export const getVitalsStatsWide = async (patId: string, range: '24h' | '7d' = '7d'): Promise<VitalsStatsWideRow | null> => {
  try {
    const qs = new URLSearchParams({ pat_id: patId, range });
    const data = await apiCall(`/vitals-stats-wide?${qs.toString()}`);
    return (data as VitalsStatsWideRow | null) ?? null;
  } catch (error) {
    console.warn('Error fetching vitals stats wide for patient', patId, range, error);
    return null;
  }
};

export const getServerHealth = async (): Promise<{ status: string; timestamp: string }> => {
  try {
    const data = await apiCall('/health');
    return data as any;
  } catch (error) {
    throw error;
  }
};

// --- ECG endpoints ---
export const getLatestEcgSamples = async (): Promise<Array<{ sample_index: number; ecg1: number; timestamp: string }>> => {
  try {
    const data = await apiCall('/ecg/latest-samples');
    return data as any[];
  } catch (error) {
    console.error('Error fetching latest ECG samples:', error);
    throw error;
  }
};

export const getLatestEcg2Samples = async (): Promise<Array<{ sample_index: number; ecg2: number; timestamp: string }>> => {
  try {
    const data = await apiCall('/ecg2/latest-samples');
    return data as any[];
  } catch (error) {
    console.error('Error fetching latest ECG2 samples:', error);
    throw error;
  }
};

// --- Demo RAW endpoints ---
export const getLatestDemoRawSamples = async (): Promise<Array<{ timestamp: string; sample_index?: number; ecg1?: number; ecg2?: number; green_ppg?: number; yellow_ppg?: number }>> => {
  try {
    const data = await apiCall('/demo-raw/latest');

    
    return data as any[];
  } catch (error) {
    console.error('Error fetching latest demo raw samples:', error);
    throw error;
  }
};

export const getDemoRawSamplesByTimestamp = async (timestampIso: string): Promise<Array<{ timestamp: string; sample_index?: number; ecg1?: number; ecg2?: number; green_ppg?: number; yellow_ppg?: number }>> => {
  try {
    const qs = new URLSearchParams({ timestamp: timestampIso });
    const data = await apiCall(`/demo-raw/by-timestamp?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching demo raw samples by timestamp:', error);
    throw error;
  }
};

export const getRawSamplesByTimestamp = async (
  timestampIso: string,
  patId?: string
): Promise<Array<{ timestamp: string; sample_index?: number; ecg1?: number; ecg2?: number; ppg_green?: number; ppg_yellow?: number; green_ppg?: number; yellow_ppg?: number }>> => {
  try {
    const qs = new URLSearchParams({ timestamp: timestampIso });
    if (patId) qs.set('pat_id', patId);
    const data = await apiCall(`/raw/by-timestamp?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching raw samples by timestamp:', error);
    throw error;
  }
};

export const getLatestPpgGreenSamples = async (): Promise<Array<{ sample_index: number; ppg_green: number; timestamp: string }>> => {
  try {
    const data = await apiCall('/ppg-green/latest-samples');
    return data as any[];
  } catch (error) {
    console.error('Error fetching latest PPG green samples:', error);
    throw error;
  }
};

export const getLatestPpgYellowSamples = async (): Promise<Array<{ sample_index: number; ppg_yellow: number; timestamp: string }>> => {
  try {
    const data = await apiCall('/ppg-yellow/latest-samples');
    return data as any[];
  } catch (error) {
    console.error('Error fetching latest PPG yellow samples:', error);
    throw error;
  }
};

// --- Raw samples by time range ---
export const getEcgRangeSamples = async (startIso: string, endIso: string): Promise<Array<{ sample_index: number; ecg1: number; timestamp: string }>> => {
  try {
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    const data = await apiCall(`/ecg/range-samples?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching ECG range samples:', error);
    throw error;
  }
};

export const getEcg2RangeSamples = async (startIso: string, endIso: string): Promise<Array<{ sample_index: number; ecg2: number; timestamp: string }>> => {
  try {
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    const data = await apiCall(`/ecg2/range-samples?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching ECG2 range samples:', error);
    throw error;
  }
};

export const getPpgGreenRangeSamples = async (startIso: string, endIso: string): Promise<Array<{ sample_index: number; ppg_green: number; timestamp: string }>> => {
  try {
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    const data = await apiCall(`/ppg-green/range-samples?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching PPG Green range samples:', error);
    throw error;
  }
};

export const getPpgYellowRangeSamples = async (startIso: string, endIso: string): Promise<Array<{ sample_index: number; ppg_yellow: number; timestamp: string }>> => {
  try {
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    const data = await apiCall(`/ppg-yellow/range-samples?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching PPG Yellow range samples:', error);
    throw error;
  }
};

export const getLatestDataTimestamps = async (): Promise<{ ecg_ts: string | null; ecg2_ts: string | null; ppg_ts: string | null; ppg_yellow_ts: string | null; server_time: string }> => {
  try {
    const data = await apiCall('/data/latest-timestamps');
    return data as any;
  } catch (error) {
    console.error('Error fetching latest timestamps:', error);
    throw error;
  }
};

// Helper function to show what the actual API calls would look like
export const getApiEndpoints = () => {
  return {
    get_vital_signs: 'GET /api/vital-signs?limit={limit}',
    get_vital_signs_range: 'GET /api/vital-signs/range?start={iso}&end={iso}',
    health_check: 'GET /api/health'
  };
};

// --- PPG Green endpoints ---
export const getPpgGreenRangeHours = async (hours: number): Promise<any[]> => {
  try {
    console.log('Fetching PPG Green for last hours:', hours);
    const data = await apiCall(`/ppg/green/range-hours?hours=${encodeURIComponent(hours)}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching PPG Green range-hours:', error);
    throw error;
  }
};

// --- Temperature endpoints ---
export const getTemperatureRangeHours = async (hours: number, patId?: string): Promise<Array<{ timestamp: string; sample_index: number; temp: number }>> => {
  try {
    // console.log('Fetching Temperature for last hours:', hours, 'patId:', patId);
    let url = `/temperature/range-hours?hours=${encodeURIComponent(hours)}`;
    if (patId) {
      url += `&pat_id=${encodeURIComponent(patId)}`;
    }
    // console.log('Temperature API URL:', url);
    const data = await apiCall(url);
    // console.log('Temperature API response:', data);
    return data as any[];
  } catch (error) {
    console.error('Error fetching Temperature range-hours:', error);
    throw error;
  }
};

export const getTemperatureRange = async (startIso: string, endIso: string, patId?: string): Promise<Array<{ timestamp: string; sample_index: number; temp: number }>> => {
  try {
    console.log('Fetching Temperature in range:', startIso, '→', endIso);
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/temperature/range?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching Temperature range:', error);
    throw error;
  }
};

export const getLatestTemperature = async (patId?: string): Promise<{ timestamp: string; sample_index: number; temp: number } | null> => {
  try {
    let url = `/temperature/latest`;
    if (patId) {
      url += `?pat_id=${encodeURIComponent(patId)}`;
    }
    const data = await apiCall(url);
    return (data as any) ?? null;
  } catch (error) {
    console.error('Error fetching latest Temperature:', error);
    throw error;
  }
};

export const getLatestSpo2 = async (): Promise<{ timestamp: string; spo2: number } | null> => {
  try {
    const data = await apiCall(`/spo2/latest`);
    return (data as any) ?? null;
  } catch (error) {
    console.error('Error fetching latest SpO2:', error);
    throw error;
  }
};

// --- Doctors & Patients ---
/** Doctor preferences: one object { pat_id: 7|24, ... }; key order = display order (stored as TEXT in DB so order is preserved). */
export type DoctorPreferences = Record<string, number>;

export const getDoctorByEmail = async (email: string): Promise<{ doc_id: string | null; preferences?: DoctorPreferences | string }> => {
  try {
    const data = await apiCall(`/doctors/by-email?email=${encodeURIComponent(email)}`);
    return data as any;
  } catch (error) {
    console.error('Error fetching doctor by email:', error);
    throw error;
  }
};

export const updateDoctorPreferences = async (docId: string, preferences: DoctorPreferences): Promise<{ success: boolean }> => {
  try {
    const data = await apiCall('/doctors/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ doc_id: docId, preferences }),
    });
    return data as any;
  } catch (error) {
    console.error('Error updating doctor preferences:', error);
    throw error;
  }
};

export const getPatientsByDoctor = async (docId: string): Promise<Array<{ pat_id: string; first_name: string; last_name: string; dev_id: string; email?: string; doctor_notes?: string; amka?: string }>> => {
  try {
    const data = await apiCall(`/patients/by-doctor?doc_id=${encodeURIComponent(docId)}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching patients by doctor:', error);
    throw error;
  }
};

export type PhoneErrorReport = {
  id: string;
  email: string;
  timestamp: string;
  apps_version: string;
  firmware_version: string | null;
  python_processing_version: string | null;
  page: string;
  operation: string;
  device_id: string | null;
  phone: string | null;
  network: string | null;
  error: string;
  created_at: string;
  pat_id: string;
  first_name: string;
  last_name: string;
};

export type PhoneErrorReportsResponse = {
  success: boolean;
  total: number;
  limit: number;
  offset: number;
  errors: PhoneErrorReport[];
};

export type PhoneErrorReportsQuery = {
  pat_id?: string;
  email?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
};

export const getPhoneErrorReports = async (
  query: PhoneErrorReportsQuery = {}
): Promise<PhoneErrorReportsResponse> => {
  try {
    const params = new URLSearchParams();
    if (query.pat_id) params.set('pat_id', query.pat_id);
    if (query.email) params.set('email', query.email);
    if (query.start) params.set('start', toWallClockApiIso(query.start));
    if (query.end) params.set('end', toWallClockApiIso(query.end));
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.offset != null) params.set('offset', String(query.offset));

    const qs = params.toString();
    const data = await apiCall(`/web/phone-errors${qs ? `?${qs}` : ''}`);
    return data as PhoneErrorReportsResponse;
  } catch (error) {
    console.error('Error fetching phone error reports:', error);
    throw error;
  }
};

export type LoginAttempt = {
  id: number;
  email: string;
  ip_address: string;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
  created_at: string;
  ip_info: Record<string, unknown> | null;
};

export type LoginAttemptsResponse = {
  success: boolean;
  total: number;
  limit: number;
  offset: number;
  attempts: LoginAttempt[];
};

export type LoginAttemptsQuery = {
  email?: string;
  ip_address?: string;
  success?: boolean;
  failure_reason?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
};

export const getLoginAttempts = async (
  query: LoginAttemptsQuery = {}
): Promise<LoginAttemptsResponse> => {
  try {
    const params = new URLSearchParams();
    if (query.email) params.set('email', query.email);
    if (query.ip_address) params.set('ip_address', query.ip_address);
    if (query.success != null) params.set('success', query.success ? 'true' : 'false');
    if (query.failure_reason) params.set('failure_reason', query.failure_reason);
    if (query.start) params.set('start', toWallClockApiIso(query.start));
    if (query.end) params.set('end', toWallClockApiIso(query.end));
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.offset != null) params.set('offset', String(query.offset));

    const qs = params.toString();
    const data = await apiCall(`/web/login-attempts${qs ? `?${qs}` : ''}`);
    return data as LoginAttemptsResponse;
  } catch (error) {
    console.error('Error fetching login attempts:', error);
    throw error;
  }
};

export type FetchRawVitalFilter = {
  metric:
    | 'heart_rate_bpm'
    | 'hrv'
    | 'respiratory_rate'
    | 'spo2'
    | 'systolic_blood_pressure'
    | 'diastolic_blood_pressure'
    | 'temperature'
    | 'activity'
    | 'activ_intensity';
  operator: '>' | '>=' | '<' | '<=' | '=' | '!=';
  value: number | string;
};

export type FetchRawQuery = {
  start: string;
  end: string;
  /** Comparison for the From timestamp; default ">=" */
  startOperator?: '>' | '>=';
  /** Comparison for the To timestamp; default "<=" */
  endOperator?: '<' | '<=';
  /** Calendar dates (YYYY-MM-DD) used in the download filename */
  fromDate: string;
  toDate: string;
  /** Browser timezone offset from UTC in hours (e.g. 3 for UTC+3) */
  tzOffsetHours?: number;
  /** Optional numeric patient ID filter */
  patId?: string;
  filters: FetchRawVitalFilter[];
  combine: 'and' | 'or';
};

const FETCH_RAW_FILENAME_METRICS: Record<FetchRawVitalFilter['metric'], string> = {
  heart_rate_bpm: 'hr',
  hrv: 'hrv',
  respiratory_rate: 'rr',
  spo2: 'spo2',
  systolic_blood_pressure: 'sbp',
  diastolic_blood_pressure: 'dbp',
  temperature: 'temp',
  activity: 'activity',
  activ_intensity: 'intensity',
};

const FETCH_RAW_FILENAME_OPS: Record<FetchRawVitalFilter['operator'], string> = {
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  '=': 'eq',
  '!=': 'neq',
};

function formatFetchRawFilenameDay(yyyyMmDd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yyyyMmDd || '').trim());
  if (!match) return '';
  return `${Number(match[3])}-${Number(match[2])}`;
}

export function buildFetchRawCsvFilename(
  exportType: 'vitals' | 'raw' | 'accel' | '32hz',
  query: FetchRawQuery
): string {
  const prefix = exportType === 'raw'
    ? 'Raw'
    : exportType === 'accel'
      ? 'Accel'
      : exportType === '32hz'
        ? 'PpgAccelRaw'
      : 'Vitals';
  const combine = query.combine === 'or' ? 'OR' : 'AND';
  const filterParts = query.filters.map((filter) => {
    const metric = FETCH_RAW_FILENAME_METRICS[filter.metric];
    const op = FETCH_RAW_FILENAME_OPS[filter.operator];
    return `${metric}-${op}-${filter.value}`;
  });
  const filterSection = filterParts.length > 0 ? filterParts.join(`_${combine}_`) : 'nofilters';
  const patSection = query.patId?.trim()
    ? `pat${query.patId.trim().replace(/[^a-zA-Z0-9_-]/g, '_')}`
    : '';
  const fromPart = formatFetchRawFilenameDay(query.fromDate);
  const toPart = formatFetchRawFilenameDay(query.toDate);
  const dateSection = fromPart && toPart ? `${fromPart}_${toPart}` : 'nodates';
  const nameParts = [prefix, patSection, filterSection, dateSection].filter(Boolean);
  return nameParts.join('_') + '.csv'
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 180);
}

function getFetchRawQueryParams(query: FetchRawQuery): URLSearchParams {
  const params = new URLSearchParams({
    start: query.start,
    end: query.end,
    startOperator: query.startOperator || '>=',
    endOperator: query.endOperator || '<=',
    fromDate: query.fromDate,
    toDate: query.toDate,
    combine: query.combine,
    filters: JSON.stringify(query.filters)
  });
  const patId = query.patId?.trim();
  if (patId) {
    params.set('pat_id', patId);
  }
  if (query.tzOffsetHours != null && Number.isFinite(query.tzOffsetHours)) {
    params.set('tz_offset_hours', String(query.tzOffsetHours));
  }
  return params;
}

export async function getFilteredFetchRawCounts(query: FetchRawQuery): Promise<{
  vitals_count: number;
  distinct_minute_count: number;
  minute_slots_in_range: number | null;
  missing_minute_slots: number | null;
  raw_count: number;
  accel_count: number;
  ppg_accel_count: number;
  sleep_count: number;
}> {
  const data = await apiCall(`/web/fetchraw/count?${getFetchRawQueryParams(query).toString()}`);
  return data as {
    vitals_count: number;
    distinct_minute_count: number;
    minute_slots_in_range: number | null;
    missing_minute_slots: number | null;
    raw_count: number;
    accel_count: number;
    ppg_accel_count: number;
    sleep_count: number;
  };
}

export async function downloadFilteredFetchRawCsv(
  exportType: 'vitals' | 'raw' | 'accel' | '32hz',
  query: FetchRawQuery
): Promise<void> {
  await downloadExportJson(
    apiUrl(`/web/fetchraw/${exportType}.csv?${getFetchRawQueryParams(query).toString()}`),
    buildFetchRawCsvFilename(exportType, query),
    `Failed to download filtered ${exportType} CSV`
  );
}

export const getUserRole = async (email: string): Promise<{ role: string }> => {
  try {
    const data = await apiCall(`/users/role?email=${encodeURIComponent(email)}`);
    return data as any;
  } catch (error) {
    console.error('Error fetching user role:', error);
    throw error;
  }
};

export const getPatientByEmail = async (email: string): Promise<{ pat_id: string }> => {
  try {
    const data = await apiCall(`/patients/by-email?email=${encodeURIComponent(email)}`);
    return data as any;
  } catch (error) {
    console.error('Error fetching patient by email:', error);
    throw error;
  }
};

export const getPatientInfo = async (patId: string): Promise<{ pat_id: string; first_name: string; last_name: string; dev_id: string; email?: string }> => {
  try {
    const data = await apiCall(`/patient-info?pat_id=${encodeURIComponent(patId)}`);
    return data as any;
  } catch (error) {
    console.error('Error fetching patient info:', error);
    throw error;
  }
};

export const getPatientBedTime = async (patId: string): Promise<{ pat_id: string; bed_time: string | null }> => {
  try {
    const data = await apiCall(`/patient-bed-time?pat_id=${encodeURIComponent(patId)}`);
    return data as { pat_id: string; bed_time: string | null };
  } catch (error) {
    console.error('Error fetching patient bed time:', error);
    throw error;
  }
};

export type SleepConsistencyHistoryRow = { timestamp: string; activity?: string | null };

export const getSleepConsistencyHistory = async (
  beforeIso: string,
  options: { patId?: string; nights?: number; demo?: boolean } = {}
): Promise<{
  before: string;
  nights: number;
  start: string;
  end: string;
  rows: SleepConsistencyHistoryRow[];
}> => {
  try {
    const qs = new URLSearchParams({
      before: beforeIso,
      nights: String(options.nights ?? 13),
    });
    if (options.patId) qs.append('pat_id', options.patId);
    if (options.demo) qs.append('demo', '1');
    const data = await apiCall(`/sleep-consistency-history?${qs.toString()}`);
    return data as {
      before: string;
      nights: number;
      start: string;
      end: string;
      rows: SleepConsistencyHistoryRow[];
    };
  } catch (error) {
    console.error('Error fetching sleep consistency history:', error);
    throw error;
  }
};

export const getPatientNotes = async (patId: string): Promise<{ doctor_notes: string }> => {
  try {
    const data = await apiCall(`/patient-notes?pat_id=${encodeURIComponent(patId)}`);
    return data as any;
  } catch (error) {
    console.error('Error fetching patient notes:', error);
    throw error;
  }
};

export const updatePatientNotes = async (patId: string, doctorNotes: string): Promise<{ success: boolean; message: string }> => {
  try {
    const data = await apiCall('/patient-notes', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pat_id: patId,
        doctor_notes: doctorNotes
      })
    });
    return data as any;
  } catch (error) {
    console.error('Error updating patient notes:', error);
    throw error;
  }
};

// --- Demo Vitals endpoints ---
export const getDemoVitalsRange = async (startIso: string, endIso: string): Promise<Array<{ timestamp: string; temperature: number; breathing: number; heart_rate: number; heart_rate_variability: number; systolic: number; diastolic: number; spo2: number; sleep_stage?: string; activity?: string; activ_intensity?: number | string | null }>> => {
  try {
    console.log('Fetching demo vitals in range:', startIso, '→', endIso);
    const data = await apiCall(`/demo-vitals/range?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching demo vitals range:', error);
    throw error;
  }
};

// --- Real Vitals (from vital_signs_results) ---
export const getVitalsRange = async (startIso: string, endIso: string, patId?: string): Promise<Array<{ timestamp: string; temperature?: number; breathing?: number; heart_rate?: number; heart_rate_variability?: number; systolic?: number; diastolic?: number; spo2?: number; activity?: string; activ_intensity?: number | string | null }>> => {
  try {
    console.log('Fetching real vitals in range:', startIso, '→', endIso);
    const qs = new URLSearchParams({ start: startIso, end: endIso });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/vital-signs/range?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching real vitals range:', error);
    throw error;
  }
};

export const getDemoVitalsRangeHours = async (hours: number): Promise<Array<{ timestamp: string; temperature: number; breathing: number; heart_rate: number; heart_rate_variability: number; systolic: number; diastolic: number; spo2: number; sleep_stage?: string }>> => {
  try {
    console.log('Fetching demo vitals for last hours:', hours);
    const data = await apiCall(`/demo-vitals/range-hours?hours=${encodeURIComponent(hours)}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching demo vitals range-hours:', error);
    throw error;
  }
};

export const getLatestDemoVitals = async (): Promise<{ timestamp: string; temperature: number; breathing: number; heart_rate: number; heart_rate_variability: number; systolic: number; diastolic: number; spo2: number } | null> => {
  try {
    const data = await apiCall(`/demo-vitals/latest`);
    return (data as any) ?? null;
  } catch (error) {
    console.error('Error fetching latest demo vitals:', error);
    throw error;
  }
};

/**
 * Normalize a datetime value to UTC ISO string for API requests.
 * - Date input -> toISOString()
 * - ISO string with timezone -> parsed and normalized to Z
 * - Local datetime string without timezone -> interpreted as local and converted to UTC
 */
export const toWallClockApiIso = (input: string | Date): string => {
  if (input instanceof Date) {
    return input.toISOString();
  }

  const raw = String(input).trim();
  if (!raw) return raw;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toISOString();
};

export const getDemoCriticalEventsRange = async (startIso: string, endIso: string, patId?: string): Promise<Array<{ timestamp: string; type: string; value?: string | number | null }>> => {
  try {
    const qs = new URLSearchParams({
      start: toWallClockApiIso(startIso),
      end: toWallClockApiIso(endIso),
    });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/demo-critical-events/range?${qs.toString()}`);
    return data as any[];
  } catch (error) {
    console.error('Error fetching demo critical events:', error);
    throw error;
  }
};

// --- Real Critical Events (from critical_events table) ---
export const getCriticalEventsRange = async (startIso: string, endIso: string, patId?: string): Promise<Array<{ timestamp: string; type: string; value?: string | number | null }>> => {
  try {
    const startWallClock = toWallClockApiIso(startIso);
    const endWallClock = toWallClockApiIso(endIso);
    console.log('[FE] fetch real critical events (UTC):', startWallClock, '→', endWallClock);
    const qs = new URLSearchParams({
      start: startWallClock,
      end: endWallClock,
    });
    if (patId) qs.append('pat_id', patId);
    const data = await apiCall(`/critical-events/range?${qs.toString()}`);
    console.log('[FE] critical events rows:', Array.isArray(data) ? data.length : 'n/a');
    return data as any[];
  } catch (error) {
    console.error('Error fetching critical events:', error);
    throw error;
  }
};

export const createDemoVitalsTable = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const data = await apiCall('/demo-vitals/create-table', { method: 'POST' });
    return data as any;
  } catch (error) {
    console.error('Error creating demo vitals table:', error);
    throw error;
  }
};

export const insertSampleDemoData = async (): Promise<{ success: boolean; message: string; count: number }> => {
  try {
    const data = await apiCall('/demo-vitals/insert-sample-data', { method: 'POST' });
    return data as any;
  } catch (error) {
    console.error('Error inserting sample demo data:', error);
    throw error;
  }
};

export type FhirBundle = {
  resourceType: 'Bundle';
  type: string;
  timestamp: string;
  entry: Array<{ fullUrl: string; resource: Record<string, unknown> }>;
};

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i);
  return match ? decodeURIComponent(match[1].trim()) : null;
}

async function downloadExportJson(requestUrl: string, fallbackFilename: string, errorPrefix: string): Promise<void> {
  const response = await fetchWithRetry(requestUrl, {
    headers: withAuthHeaders()
  });
  if (!response.ok) {
    const text = await response.text();
    let message = errorPrefix;
    try {
      const j = JSON.parse(text);
      if (j?.error) message = j.error;
    } catch {
      if (text) message = text;
    }
    reportInvalidAuthIfNeeded(response.status, message);
    throw new Error(message);
  }
  const blob = await response.blob();
  const filename = parseContentDispositionFilename(response.headers.get('Content-Disposition'))
    || fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

export type PatientShareUrlResult = {
  url: string;
  token: string;
  expires_at: string;
  expires_in: number;
  patient_id: string;
  start_at: string;
  end_at: string;
};

export async function generatePatientTimeBoundShareUrl(
  patId: string,
  expiresInSeconds: number,
  startIso: string,
  endIso: string,
  description: string
): Promise<PatientShareUrlResult> {
  const data = await apiCall('/Patients/share-url', {
    method: 'POST',
    body: JSON.stringify({
      pat_id: patId,
      expires_in: expiresInSeconds,
      start: startIso,
      end: endIso,
      description
    })
  });
  return data as PatientShareUrlResult;
}

export async function generatePatientOneTimeShareUrl(
  patId: string,
  expiresInSeconds: number,
  startIso: string,
  endIso: string,
  description: string
): Promise<PatientShareUrlResult> {
  const data = await apiCall('/Patients/share-url/one-time', {
    method: 'POST',
    body: JSON.stringify({
      pat_id: patId,
      expires_in: expiresInSeconds,
      start: startIso,
      end: endIso,
      description
    })
  });
  return data as PatientShareUrlResult;
}

/** Download FHIR export JSON returned by the API (no client-side transformation). */
export async function downloadWebPatientFhirExport(
  patId: string,
  startIso: string,
  endIso: string
): Promise<void> {
  const datePart = new Date().toISOString().slice(0, 10);
  const fallbackFilename = `patient-${patId}-fhir-${datePart}.json`;
  await downloadExportJson(
    `/api/web/patient-data?pat_id=${encodeURIComponent(patId)}&start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`,
    fallbackFilename,
    'Failed to export patient data'
  );
}

/** Download human-readable JSON payload without FHIR coding fields. */
export async function downloadWebPatientRawExport(
  patId: string,
  startIso: string,
  endIso: string
): Promise<void> {
  const datePart = new Date().toISOString().slice(0, 10);
  const fallbackFilename = `patient-${patId}-raw-${datePart}.json`;
  await downloadExportJson(
    `/api/web/patient-data/raw?pat_id=${encodeURIComponent(patId)}&start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`,
    fallbackFilename,
    'Failed to export patient raw data'
  );
}
